import { 
  AlertCircle, Calendar, Mail, MapPin, MessageSquare, 
  Phone, User, CheckCircle, XCircle, Package 
} from 'lucide-react';
import { getStatusBadge } from './helpers';

/**
 * Order details modal component
 */
export function OrderDetailsModal({ 
  order, 
  adminNotes, 
  setAdminNotes, 
  processing, 
  onClose, 
  onUpdateStatus 
}) {
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {order.order_number}
              </h2>
              <p className="text-gray-600 mt-1">
                Opprettet {new Date(order.created_at).toLocaleString('nb-NO')}
              </p>
            </div>
            {getStatusBadge(order.status)}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Priority Warning */}
          {order.priority === 'urgent' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-red-900">HASTER!</div>
                <div className="text-sm text-red-700 mt-1">Denne bestillingen er markert som hastebestilling.</div>
              </div>
            </div>
          )}

          {/* Sample Info */}
          <SampleInfoSection order={order} />

          {/* Location */}
          <LocationSection order={order} />

          {/* Contact Info */}
          <ContactSection order={order} />

          {/* Special Instructions */}
          {order.special_instructions && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Spesielle instruksjoner
              </h3>
              <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">
                {order.special_instructions}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Admin-notater</h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              placeholder="Legg til notater til kunden..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Lukk
          </button>

          {order.status === 'pending' && (
            <>
              <button
                onClick={() => onUpdateStatus(order.id, 'rejected', adminNotes)}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Avslå
              </button>
              
              <button
                onClick={() => onUpdateStatus(order.id, 'approved', adminNotes)}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Godkjenn
              </button>
            </>
          )}

          {order.status === 'approved' && (
            <button
              onClick={() => onUpdateStatus(order.id, 'completed', adminNotes)}
              disabled={processing}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              <Package className="w-5 h-5" />
              Marker som fullført
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SampleInfoSection({ order }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Prøveinformasjon</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Prøvetype</div>
          <div className="text-sm text-gray-900">
            {order.sample_type === 'algae' ? '🔬 Algeprøve' : '🦐 Zooplankton'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">
            <Calendar className="inline w-3 h-3 mr-1" />
            Ønsket dato
          </div>
          <div className="text-sm text-gray-900">
            {new Date(order.preferred_date).toLocaleDateString('nb-NO', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Dybde</div>
          <div className="text-sm text-gray-900">{order.depth || '-'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Prioritet</div>
          <div className="text-sm text-gray-900">
            {order.priority === 'urgent' ? '🔴 Haster' : '🟢 Normal'}
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationSection({ order }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MapPin className="w-5 h-5" />
        Lokalitet
      </h3>
      <div className="space-y-2">
        <div>
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Navn</div>
          <div className="text-sm text-gray-900">{order.location_name}</div>
        </div>
        {order.location_address && (
          <div>
            <div className="text-xs text-gray-500 uppercase font-medium mb-1">Adresse/Beskrivelse</div>
            <div className="text-sm text-gray-900">{order.location_address}</div>
          </div>
        )}
        {(order.latitude || order.longitude) && (
          <div>
            <div className="text-xs text-gray-500 uppercase font-medium mb-1">Koordinater</div>
            <div className="text-sm text-gray-900 font-mono">
              {order.latitude}, {order.longitude}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactSection({ order }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <User className="w-5 h-5" />
        Kontaktinformasjon
      </h3>
      <div className="space-y-2">
        {order.contact_person && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900">{order.contact_person}</span>
          </div>
        )}
        {order.contact_phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900">{order.contact_phone}</span>
          </div>
        )}
        {order.contact_email && (
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900">{order.contact_email}</span>
          </div>
        )}
      </div>
    </div>
  );
}
